import {has} from 'ramda';

const MEGABYTE = 1024 * 1024;

/**
 * Maximum size of every downloaded photo. `maxBytes` is the limit the user picks; `width` is the
 * storage resize width tried first for a single photo (measured: JPEG ~Q80 output of a 24 MP photo
 * at that width stays under the limit). `null` means the original file is downloaded.
 */
export const PHOTO_DOWNLOAD_SIZE_PRESETS = {
  original: {maxBytes: null, width: null, label: null},
  '5mb': {maxBytes: 5 * MEGABYTE, width: 4000, label: '5 MB'},
  '3mb': {maxBytes: 3 * MEGABYTE, width: 3200, label: '3 MB'},
  '1mb': {maxBytes: MEGABYTE, width: 1920, label: '1 MB'},
  '500kb': {maxBytes: MEGABYTE / 2, width: 1280, label: '0.5 MB'},
} as const;

/** Smallest resize width tried when the preset width is still over the limit. */
export const PHOTO_DOWNLOAD_FLOOR_WIDTH = 1024;

export const DEFAULT_PHOTO_DOWNLOAD_SIZE_PRESET = 'original';

export type PhotoDownloadSizePreset = keyof typeof PHOTO_DOWNLOAD_SIZE_PRESETS;

export const isPhotoDownloadSizePreset = (value: unknown): value is PhotoDownloadSizePreset =>
  typeof value === 'string' && has(value, PHOTO_DOWNLOAD_SIZE_PRESETS);

/** The `maxBytes` query value for bulk downloads; `undefined` (param omitted) for the original. */
export const getMaxBytes = (preset: PhotoDownloadSizePreset) =>
  PHOTO_DOWNLOAD_SIZE_PRESETS[preset].maxBytes ?? undefined;
